import * as React from 'react';
import { useState } from 'react';
import { Check, Clipboard } from '../Icon/icons';
import { Icon } from '../Icon/Icon';
import {
  frame as frameCls,
  header as headerCls,
  langStamp as langStampCls,
  copyButton as copyButtonCls,
  pre as preCls,
  inline as inlineCls,
} from './CodeBlock.css';

export interface CodeBlockProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  code: string;
  /** Optional language label rendered as a stamp in the header. */
  language?: string;
  /** Render as a single inline block instead of multi-line. */
  inline?: boolean;
  /** Hide the copy button. Default: shown. */
  hideCopy?: boolean;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * CodeBlock — monospaced code display. Two modes:
 *   - Multi-line (default): bordered card with optional language stamp
 *     and a copy-to-clipboard button.
 *   - `inline`: single inline `<code>` snippet, no chrome.
 *
 * Copy button uses the Clipboard API; falls through silently if
 * unavailable. Visual confirmation flips to a checkmark for 1.5s.
 */
export function CodeBlock({
  code,
  language,
  inline = false,
  hideCopy = false,
  ref,
  className,
  ...rest
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  if (inline) {
    return (
      <code className={[inlineCls, className].filter(Boolean).join(' ')} {...rest}>
        {code}
      </code>
    );
  }

  const onCopy = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(code);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard not available — fall through silently */
    }
  };

  return (
    <div ref={ref} className={[frameCls, className].filter(Boolean).join(' ')} {...rest}>
      {(language || !hideCopy) && (
        <div className={headerCls}>
          <span className={langStampCls}>{language ?? ''}</span>
          {!hideCopy && (
            <button type="button" onClick={onCopy} aria-label="Copy code" className={copyButtonCls}>
              {copied ? <Icon as={Check} size="sm" /> : <Icon as={Clipboard} size="sm" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>
      )}
      <pre className={preCls}>
        <code>{code}</code>
      </pre>
    </div>
  );
}
CodeBlock.displayName = 'CodeBlock';
